import React, { memo } from 'react';
import Image from 'next/image';
import { Box, Text } from '@ui-library';

import { NavLink } from '../navigation/Navigation';

import { Section } from '@/models';
import { GUEST_PORTAL_IMAGE_QUALITY } from '@/constants/images';

type HeroProps = {
  company?: string;
  guestBookSlug?: string;
  section: Pick<Section, 'plain_text' | 'settings'>;
  isKioskMode?: boolean;
  variant?: 'fullScreen' | 'header';
  sizes?: string;
};

type HeroContentProps = {
  section: Pick<Section, 'plain_text' | 'settings'>;
  variant?: 'fullScreen' | 'header';
  sizes?: string;
};

const HEADER_HERO_SIZES = '(max-width: 768px) 100vw, 67vw';

const HeroContent = memo<HeroContentProps>(function HeroContent({
  section,
  variant = 'fullScreen',
  sizes,
}) {
  const isFullScreenVariant = variant === 'fullScreen';

  const height = isFullScreenVariant
    ? '100vh'
    : section.settings.image_height
      ? `${section.settings.image_height}px`
      : '200px';

  return (
    <Box
      position="relative"
      width="100%"
      height={isFullScreenVariant ? height : { base: '200px', md: height }}
      overflow="hidden"
      display={section.plain_text ? 'flex' : 'block'}
      alignItems={section.plain_text ? 'flex-end' : undefined}
    >
      {section.settings.image_url && (
        <Image
          src={section.settings.image_url}
          alt={section.plain_text || 'Hero image'}
          fill
          sizes={sizes ?? (isFullScreenVariant ? '100vw' : HEADER_HERO_SIZES)}
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          priority
          quality={GUEST_PORTAL_IMAGE_QUALITY}
        />
      )}

      {/* Gradient Overlay */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        background={
          isFullScreenVariant
            ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.00) 100%)'
            : 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 63.86%, #000 100%)'
        }
        zIndex="0"
      />

      {Boolean(section.plain_text) && (
        <>
          <Box
            zIndex="1"
            padding={isFullScreenVariant ? 10 : 4}
            alignSelf={isFullScreenVariant ? 'center' : 'flex-end'}
            width={isFullScreenVariant ? 'auto' : 'full'}
            maxWidth={isFullScreenVariant ? { lg: '3/5', md: '1/2' } : 'full'}
            textAlign={isFullScreenVariant ? 'left' : 'center'}
          >
            <Text
              fontSize={isFullScreenVariant ? '5xl' : 'sm'}
              fontWeight={isFullScreenVariant ? 'medium' : 'semibold'}
              color="white"
            >
              {section.plain_text}
            </Text>
          </Box>
        </>
      )}
    </Box>
  );
});

const Hero = ({
  section,
  company,
  guestBookSlug,
  isKioskMode,
  variant = 'fullScreen',
  sizes,
}: HeroProps) => {
  if (section.settings.value && section.settings.type) {
    return (
      <NavLink
        block={section}
        company={company}
        guestBookSlug={guestBookSlug}
        isKioskMode={isKioskMode}
      >
        <HeroContent section={section} variant={variant} sizes={sizes} />
      </NavLink>
    );
  } else {
    return <HeroContent section={section} variant={variant} sizes={sizes} />;
  }
};

export default memo(Hero);
